import { useMutationState } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackSnackbar } from '../../../components/FeedbackSnackbar';
import { isUserFacingMutationError } from '../../../lib/queryClient';
import { settingsMutationKey } from '../api/settingsApi';

type Toast = { variant: 'success' | 'error'; message: string };

export function SaveFeedback(): ReactElement {
    const { t } = useTranslation();
    const lastStatus = useMutationState({
        filters: { mutationKey: [...settingsMutationKey] },
        select: (m) => ({ status: m.state.status, submittedAt: m.state.submittedAt, error: m.state.error }),
    });

    const [toast, setToast] = useState<Toast | null>(null);
    const [lastSeenAt, setLastSeenAt] = useState(0);

    useEffect(() => {
        const successToast: Toast = { variant: 'success', message: t('SETTINGS.TOAST.SAVED') };
        const errorToast: Toast = { variant: 'error', message: t('SETTINGS.TOAST.SAVE_ERROR') };

        let latest: { status: 'success' | 'error'; submittedAt: number; error: unknown } | null = null;
        for (const s of lastStatus) {
            if (s.status !== 'success' && s.status !== 'error') {
                continue;
            }
            if (!latest || s.submittedAt > latest.submittedAt) {
                latest = { status: s.status, submittedAt: s.submittedAt, error: s.error };
            }
        }
        if (!latest || latest.submittedAt <= lastSeenAt) {
            return;
        }
        if (latest.status === 'error' && isUserFacingMutationError(latest.error)) {
            setLastSeenAt(latest.submittedAt);
            return;
        }
        setLastSeenAt(latest.submittedAt);
        setToast(latest.status === 'success' ? successToast : errorToast);
    }, [lastStatus, lastSeenAt, t]);

    return (
        <FeedbackSnackbar
            open={toast !== null}
            severity={toast?.variant ?? 'success'}
            message={toast?.message ?? ''}
            onClose={() => setToast(null)}
        />
    );
}
